/**
 * API Response Transformers
 *
 * Converts backend API responses (UUID, snake_case, NamedReference objects)
 * into the shape the frontend components expect (camelCase, flat strings).
 */

import { childDisplayName } from 'utils/childDisplayName';

// Helper: safely extract name from a NamedReference object ({ id, name }) or return fallback
const refName = (ref, fallback = '') => (ref && ref.name) || fallback;
const refId = (ref) => (ref && ref.id) || null;

// Helper: calculate age from date_of_birth string
const calcAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
};

// Helper: format time from ISO datetime or "HH:MM" string
const formatTime = (dt) => {
  if (!dt) return '';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Helper: gradient class based on index or name
const gradientClasses = ['primary', 'secondary', 'success', 'warning', 'error', 'info'];
const getGradient = (index) => gradientClasses[index % gradientClasses.length];

// Grace period before a session whose window has closed is treated as missed.
// Keeps a session that just ended from flipping the moment the clock passes it,
// while the therapist is still writing it up. Mirrors MISSED_GRACE_MINUTES in
// backend/app/schemas/clinical.py — change both together.
export const MISSED_GRACE_MINUTES = 15;

// Statuses that mean "nothing has happened yet". Only these can go stale.
const OPEN_SESSION_STATUSES = ['upcoming', 'scheduled', 'confirmed'];

/**
 * Derive the status to *display* for a session.
 *
 * The backend stores a session as 'scheduled' and nothing ever ages that value,
 * so a session nobody ran still reports 'scheduled' months later and the UI
 * renders it as "Upcoming". This maps that stale case to 'missed'.
 *
 * Display only — the caller keeps the raw `status` for action gating (delete is
 * offered for scheduled sessions, View Data is not), which must stay tied to
 * what the backend actually believes.
 *
 * scheduled_start/scheduled_end are stored naive (no timezone) and written as
 * the author's wall-clock time, so `new Date(...)` parsing them as browser-local
 * is the intended reading. With US and India users on one instance a session
 * authored in another zone is compared against the viewer's clock; the grace
 * period absorbs minutes, not the IST/ET gap, so treat a session within a few
 * hours of its end as approximate.
 *
 * @param {string} status - mapped frontend status ('upcoming', 'completed', ...)
 * @param {object} opts
 * @param {string} [opts.scheduledEnd] - ISO datetime, preferred signal
 * @param {string} [opts.sessionDate] - 'YYYY-MM-DD', fallback when no end time
 * @param {string} [opts.actualStart] - set means work began; never mark missed
 * @returns {string} status to render
 */
export const deriveSessionDisplayStatus = (status, { scheduledEnd = null, sessionDate = null, actualStart = null } = {}) => {
  // "In Progress" means the session has STARTED and the current time is still
  // between its start and end. Once a started-but-never-ended session runs past
  // its scheduled end, it is no longer in progress — it is stuck awaiting its data
  // submission, so surface it as 'pending-submission' (red) instead of an
  // ever-green "In Progress". Only the scheduled end can tell us this; with no
  // usable end time we can't know, so it stays 'in-progress'. No grace period:
  // the rule is simply "now is past the session's end time".
  if (status === 'in-progress' && scheduledEnd) {
    const end = new Date(scheduledEnd);
    if (!isNaN(end.getTime()) && end.getTime() < Date.now()) {
      return 'pending-submission';
    }
  }

  if (!OPEN_SESSION_STATUSES.includes(status)) return status;
  // Started but never closed out is a different problem (stuck session), not a
  // missed one — leave it alone rather than mislabel it.
  if (actualStart) return status;

  const now = Date.now();

  if (scheduledEnd) {
    const end = new Date(scheduledEnd);
    if (!isNaN(end.getTime())) {
      return end.getTime() + MISSED_GRACE_MINUTES * 60000 < now ? 'missed' : status;
    }
  }

  // No usable end time: fall back to whole-day granularity so we only call a
  // session missed once its entire date has passed in the viewer's timezone.
  if (sessionDate) {
    const [y, m, d] = String(sessionDate).split('-').map(Number);
    if (y && m && d) {
      return new Date(y, m - 1, d, 23, 59, 59, 999).getTime() < now ? 'missed' : status;
    }
  }

  return status;
};

/**
 * Transform a ClientResponse from the backend into the shape the frontend expects.
 * Backend shape: { id (UUID), client_code, person_id, person: { id, name }, ... }
 * Frontend shape: { id, name, displayId, initials, age, ... }
 */
export const transformClient = (apiClient, index = 0) => {
  if (!apiClient) return null;

  const personName = refName(apiClient.person, apiClient.first_name || '');
  const firstName = apiClient.first_name || personName.split(' ')[0] || '';
  const lastName = apiClient.last_name || personName.split(' ').slice(1).join(' ') || '';
  const clientCode = apiClient.client_code || '';
  // Full display name: first + last (last may be empty for some records).
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    id: apiClient.id,
    // MASKED for display. The client code stays readable - it is the identifier
    // people work from and it identifies nobody on its own. `firstName` and
    // `lastName` below are left RAW: they feed report and document generation,
    // where the real name is required. See utils/maskChildName.
    // Preferred name in the clear when the family gave one; otherwise the legal name,
    // masked exactly as before. childDisplayName owns that rule - see utils/childDisplayName.
    name: `${clientCode} (${childDisplayName(apiClient, { legalName: fullName })})`,
    displayId: `#${clientCode}`,
    clientCode,
    firstName,
    lastName,
    initials: firstName.charAt(0).toUpperCase(),
    age: calcAge(apiClient.date_of_birth) || apiClient.age || null,
    gender: apiClient.gender || null,
    gradientClass: getGradient(index),
    startDate: apiClient.enrollment_date || null,
    diagnosis: [],
    enrolledPrograms: [],
    activeIEPVersion: null,
    iepHistory: [],
    assessmentHistory: [],
    lastSession: null,
    sessionInProgress: false,
    sessionProgress: 0,
    dataPending: false,
    activeTargets: 0,
    masteredTargets: 0,
    isActive: apiClient.is_active !== false,
    // Registration metadata (only populated by the supervisor-filtered listClients
    // branch). Lets the Children page render in-progress registrations as drafts
    // and route the supervisor back into AddStudent to finish remaining forms.
    registrationStatus: apiClient.registration_status || null,
    registrationComplete: apiClient.registration_complete !== false,
    // How the child was onboarded ('workflow' via the new onboarding route, else
    // null/legacy). Routes "edit registration" to the matching flow.
    createdVia: apiClient.created_via || null,
    // A workflow child whose registration is NOT yet complete is a DRAFT — the
    // Children page shows a Draft badge and resumes it in the workflow runtime.
    isDraft: apiClient.created_via === 'workflow' && apiClient.registration_complete === false,
    // Keep raw fields for components that need them
    _raw: apiClient
  };
};

/**
 * Transform a SessionResponse from the backend into the shape the frontend expects.
 * Backend shape: { id (UUID), client_id, session_date, scheduled_start, scheduled_end, status: { id, name }, ... }
 * Frontend shape: { id, childId, childName, time, duration, program, status, ... }
 */
export const transformSession = (apiSession) => {
  if (!apiSession) return null;

  const rawStatusName = refName(apiSession.status, 'upcoming');
  // Normalize: use code if available, otherwise lowercase the name to match statusMap keys
  const statusName = (apiSession.status && apiSession.status.code) || rawStatusName.toLowerCase().replace(/\s+/g, '_');
  const serviceName = refName(apiSession.service, '') || refName(apiSession.session_type, 'ABA Therapy');

  // Calculate duration in minutes
  let duration = apiSession.duration_minutes || 60;
  if (!apiSession.duration_minutes && apiSession.scheduled_start && apiSession.scheduled_end) {
    const start = new Date(apiSession.scheduled_start);
    const end = new Date(apiSession.scheduled_end);
    duration = Math.round((end - start) / 60000);
  }

  // Map backend status names to frontend status values
  const statusMap = {
    scheduled: 'upcoming',
    in_progress: 'in-progress',
    completed: 'completed',
    cancelled: 'cancelled',
    no_show: 'no-show',
    pending: 'pending'
  };

  // Icon mapping based on session type name or code
  const iconMap = {
    'ABA Therapy': 'puzzle-piece',
    'Speech Therapy': 'comments',
    'Occupational Therapy': 'hands',
    'Direct Therapy': 'puzzle-piece',
    'Parent Training': 'users',
    direct_therapy: 'puzzle-piece',
    supervision: 'eye',
    parent_training: 'users',
    assessment: 'clipboard-list'
  };

  // Name resolved at the transformer, not at each render site. Every calendar,
  // session card and session dialog reads `childName` from here, so one rule covers
  // them all. childDisplayName shows the family's preferred name in the clear and
  // masks a legal first/last only when no preferred name exists — the same rule
  // transformClient uses for the child cards, so the two never disagree. The client
  // code stays readable. See utils/childDisplayName and utils/maskChildName.
  const clientName = apiSession.client
    ? `${apiSession.client.client_code || ''} (${childDisplayName(apiSession.client, {
        legalName: [apiSession.client.first_name, apiSession.client.last_name].filter(Boolean).join(' ')
      })})`.trim()
    : '';

  // Extract date and time components for calendar views
  const sessionDate =
    apiSession.session_date || (apiSession.scheduled_start ? new Date(apiSession.scheduled_start).toISOString().split('T')[0] : null);
  const startTimeStr = apiSession.scheduled_start ? new Date(apiSession.scheduled_start).toTimeString().slice(0, 5) : null;
  const endTimeStr = apiSession.scheduled_end ? new Date(apiSession.scheduled_end).toTimeString().slice(0, 5) : startTimeStr;

  const clientInitials = apiSession.client
    ? (apiSession.client.first_name || apiSession.client.client_code || '').charAt(0).toUpperCase()
    : '';

  const mappedStatus = statusMap[statusName] || statusName;

  return {
    id: apiSession.id,
    childId: apiSession.client_id || (apiSession.client && apiSession.client.id) || null,
    childName: clientName,
    // The name alone, for the calendar's event tiles: a client code is hard to
    // recognise at a glance, a name is not. Same childDisplayName rule as
    // childName (preferred name in the clear, else the masked legal name); the
    // code is only the last resort, for a child with no name recorded at all.
    childLabel: apiSession.client
      ? childDisplayName(apiSession.client, {
          legalName: [apiSession.client.first_name, apiSession.client.last_name].filter(Boolean).join(' '),
          fallback: apiSession.client.client_code || ''
        })
      : '',
    childCode: apiSession.client?.client_code || '',
    // Name first, code in brackets - "ClientT (CLT-20260511-LVKX)" - for the session
    // dialogs, where the code still tells two same-named children apart but the name
    // is what a person looks for.
    childDisplay: apiSession.client
      ? (() => {
          const name = childDisplayName(apiSession.client, {
            legalName: [apiSession.client.first_name, apiSession.client.last_name].filter(Boolean).join(' ')
          });
          const code = apiSession.client.client_code || '';
          return name && code ? `${name} (${code})` : name || code;
        })()
      : '',
    childInitials: clientInitials,
    time: formatTime(apiSession.scheduled_start),
    duration,
    program: serviceName,
    status: mappedStatus,
    // What the chips should read. Diverges from `status` only when a scheduled
    // session's window has closed with nobody starting it — see
    // deriveSessionDisplayStatus. Never use this to gate actions.
    displayStatus: deriveSessionDisplayStatus(mappedStatus, {
      scheduledEnd: apiSession.scheduled_end,
      sessionDate,
      actualStart: apiSession.actual_start
    }),
    startedAt: apiSession.actual_start || null,
    completedAt: apiSession.actual_end || null,
    icon: iconMap[serviceName] || iconMap[refName(apiSession.session_type, '')] || 'puzzle-piece',
    sessionDate,
    date: sessionDate,
    startTime: startTimeStr,
    endTime: endTimeStr,
    sessionType: refName(apiSession.session_type, 'individual'),
    notes: apiSession.notes || null,
    therapistId: apiSession.therapist_id || refId(apiSession.therapist),
    therapistName: refName(apiSession.therapist, ''),
    // Non-null when the session was generated from a scheduling template;
    // the calendar UI uses it to gate the "Delete entire series" option.
    scheduledInstanceId: apiSession.scheduled_instance_id || null,
    // Shared by every occurrence created from one "repeat" action. Non-null is
    // what gates the Outlook-style "this session / this and following /
    // entire series" scope picker on edit and delete.
    seriesId: apiSession.series_id || null,
    recurrenceRule: apiSession.recurrence_rule || null,
    // Person id of whoever pressed Start. Only they may collect data against a
    // running session or end it — see canRunSession in utils/sessionRunner.
    // Null on sessions started before the column existed.
    startedBy: apiSession.started_by || null,
    _raw: apiSession
  };
};

/**
 * Transform an IepTargetResponse from the backend into the shape the frontend expects.
 * Backend shape: { id (UUID), target_code, description, status: { id, name }, ... }
 * Frontend shape: { id, name, description, domain, status, image, ... }
 */
export const transformTarget = (apiTarget, goalDomain = '') => {
  if (!apiTarget) return null;

  return {
    id: apiTarget.id,
    name: apiTarget.target_code || apiTarget.description || '',
    description: apiTarget.description || '',
    domain: goalDomain,
    status: refName(apiTarget.status, 'not_started'),
    masteryStatus: refName(apiTarget.mastery_status, 'not_started'),
    image: apiTarget.image_url || null,
    promptLevel: refName(apiTarget.current_prompt_level, ''),
    teachingProcedure: apiTarget.teaching_procedure || '',
    sdInstruction: apiTarget.sd_instruction || '',
    targetResponse: apiTarget.target_response || '',
    errorCorrection: apiTarget.error_correction || '',
    masteryCriteria: apiTarget.mastery_criteria || '',
    masteryPercentage: apiTarget.mastery_percentage || null,
    masteryConsecutiveSessions: apiTarget.mastery_consecutive_sessions || null,
    startDate: apiTarget.start_date || null,
    targetDate: apiTarget.target_date || null,
    achievedDate: apiTarget.achieved_date || null,
    displayOrder: apiTarget.display_order || 0,
    // Materials configured for this target in the supervisor IEP editor.
    // Shape: [{ source: 'central'|'child', path, name, url, category? }, ...].
    // Used by Daily Mand to populate the Quick Add cards per target.
    materials: Array.isArray(apiTarget.materials) ? apiTarget.materials : [],
    _raw: apiTarget
  };
};

/**
 * Transform an IepGoalResponse into the shape the frontend expects.
 */
export const transformIepGoal = (apiGoal) => {
  if (!apiGoal) return null;

  return {
    id: apiGoal.id,
    title: apiGoal.goal_description || apiGoal.description || '',
    goalCode: apiGoal.goal_code || '',
    domain: apiGoal.skill_area || '',
    status: refName(apiGoal.status, 'active'),
    baselineLevel: apiGoal.baseline_level || '',
    targetLevel: apiGoal.target_level || '',
    startDate: apiGoal.start_date || null,
    targetDate: apiGoal.target_date || null,
    displayOrder: apiGoal.display_order || 0,
    targets: (apiGoal.targets || []).map((t) => transformTarget(t, apiGoal.skill_area || '')),
    _raw: apiGoal
  };
};

/**
 * Transform an IEP response.
 */
export const transformIep = (apiIep) => {
  if (!apiIep) return null;

  return {
    id: apiIep.id,
    clientId: apiIep.client_id,
    version: apiIep.version_number || 1,
    status: refName(apiIep.status, 'active'),
    startDate: apiIep.start_date || null,
    endDate: apiIep.end_date || null,
    createdAt: apiIep.created_at || null,
    goals: (apiIep.goals || []).map(transformIepGoal),
    _raw: apiIep
  };
};

/**
 * Transform a mand item from the backend.
 * Backend: { id (UUID), code, name, category, image_url, ... }
 * Frontend: { id, name, category, image }
 */
export const transformMandItem = (apiItem) => {
  if (!apiItem) return null;

  return {
    id: apiItem.id,
    name: apiItem.name || apiItem.code || '',
    category: apiItem.category || '',
    image: apiItem.image_url || null,
    code: apiItem.code || '',
    _raw: apiItem
  };
};

/**
 * Transform a list of targets into cold probe target format.
 * Frontend expects: { id, name, description, image }
 */
export const transformTargetForColdProbe = (apiTarget, goalDomain = '') => {
  const t = transformTarget(apiTarget, goalDomain);
  return {
    id: t.id,
    name: t.name,
    description: t.description,
    // Carry the goal's domain so the session sheets can prefix the target name
    // with it (e.g. "Manding - Touch head").
    domain: t.domain,
    image: t.image,
    materials: t.materials,
    // Default controlling prompts (ref_prompt_level codes) — pre-selected in the
    // Cold Probe / Multi-Trial session sheets. Read straight from the API row.
    defaultControllingPrompts: Array.isArray(apiTarget?.default_controlling_prompts) ? apiTarget.default_controlling_prompts : [],
    // SD (discriminative stimulus) free-text — shown as a tooltip against the
    // target on the session sheets. transformTarget already maps sd_instruction.
    sdInstruction: t.sdInstruction || ''
  };
};

/**
 * Transform targets into target tracking format.
 * Frontend expects: { id, target, domain, dateIntroduced, dateMastered, ... }
 */
export const transformTargetForTracking = (apiTarget, goalDomain = '') => {
  const t = transformTarget(apiTarget, goalDomain);
  let daysToMastery = '-';
  if (t.startDate && t.achievedDate) {
    const days = Math.round((new Date(t.achievedDate) - new Date(t.startDate)) / (1000 * 60 * 60 * 24));
    if (days >= 0) daysToMastery = days;
  }
  return {
    id: t.id,
    target: t.description || t.name,
    domain: t.domain,
    dateIntroduced: t.startDate,
    dateMastered: t.achievedDate,
    daysToMastery,
    location: '',
    people: '',
    stimuli: ''
  };
};

/**
 * Transform targets into maintenance probe format.
 * Frontend expects: { id, name, domain, lastMastered, ... }
 */
export const transformTargetForMaintenance = (apiTarget, goalDomain = '') => {
  const t = transformTarget(apiTarget, goalDomain);
  return {
    id: t.id,
    name: t.name,
    description: t.description,
    domain: t.domain,
    lastMastered: t.achievedDate,
    status: t.status
  };
};

/**
 * Build therapist dashboard stats from sessions list.
 */
export const buildDashboardStats = (sessions = [], clients = []) => {
  const today = new Date().toISOString().split('T')[0];
  const todaySessions = sessions.filter((s) => {
    const sd = s.sessionDate || s.session_date;
    return sd === today;
  });

  const completed = todaySessions.filter((s) => s.status === 'completed').length;
  const total = todaySessions.length;

  return {
    totalSessions: total,
    assignedChildren: clients.length,
    pendingDataEntry: todaySessions.filter((s) => s.status === 'pending').length,
    completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
  };
};
